import { Entity } from '../entity';

export enum IntegrationEventDirection {
    Inbound = 'Inbound',
    Outbound = 'Outbound',
}

/**
 * View variants scope system views to a specific dashboard mount (see entity.view.repository —
 * a dashboard requesting a variant sees ONLY that variant's views, and the default mounts see none
 * of them). Used to give per-integration inbound screens their own view set without leaking views
 * onto the generic Outbound integration events page.
 */
export enum IntegrationEventResultViewVariant {
    BgisInbound = 'bgis-inbound',
    HomesNswOutbound = 'homesnsw-outbound',
}

export enum IntegrationEventResultStatus {
    Pending = 'Pending',
    Processing = 'Processing',
    Success = 'Success',
    Failed = 'Failed',
    Held = 'Held',
    /** Live and owned by its handler: prerequisites (e.g. correlated uploads) are still landing. Holds its work order like Held, keeps its own message. */
    Preparing = 'Preparing',
    Ignored = 'Ignored',
    ManuallyIgnored = 'ManuallyIgnored',
    /** Claimed, but no handler is registered for the event name — terminal, needs a code change. */
    Unhandled = 'Unhandled',
}

export interface IntegrationEventResultRecord {
    _id: string;
    integration: string;
    eventName: string;
    eventSourceId: string;
    direction: IntegrationEventDirection;
    entityType: string;
    entityId: string;
    externalReference: string;
    status: IntegrationEventResultStatus;
    message: string;
    correlationId: string;
    data: any;
    /**
     * Optional routing hint copied out of the event by the claiming integration (e.g. which kind of
     * file an upload is) so a dispatcher can order its passes without unpacking `data`. Indexed.
     */
    source?: string;
    /**
     * Structured output of the latest attempt, written in the same update as `status` — e.g. the
     * remote file id an upload produced, or a preparation summary. Read by later events that depend
     * on this one; `message` stays free text for people.
     */
    output?: Record<string, unknown>;
    updatedBy?: Entity; // The WorkBuddy user who triggered the source change, when known.
    firstHeld: Date | null;
    startedAt: Date;
    completedAt: Date | null;
    durationMs: number | null;
    createdAt: Date;
    updatedAt: Date;
    expiresAt: Date;
}

/** Commands the integration event results board can issue (see `TenantIntegrationEventResultDataStrategy.executeCommand`). */
export enum IntegrationEventResultEntityFormAction {
    IgnoreEvent = 'IgnoreEventCommand',
}

export interface IntegrationEventResultIgnoreRequest {
    resultId: string;
    reason: string;
}
